import path from 'path'

import type { DeployEvent, PlannedService, ServiceStatus } from '../operations/deploy'
import type { InitEvent } from '../operations/init'
import type { CreateServiceEvent } from '../operations/init/create-service'
import type { InitClaudeEvent, SegmentOutcome } from '../operations/init-claude'
import type { LoginEvent } from '../operations/login'
import type { LogoutEvent } from '../operations/logout'
import type { PullEvent } from '../operations/pull'
import { describeWorkspace } from '../project/auth'
import { formatBytes, plural, shortHash } from '../utils/format'
import { describeServer } from '../utils/server-name'
import { say, sayWarning, section } from './output'

// Where the terminal output lives. The operations emit events describing what happened; everything about
// how that reads — the indentation, the blank lines, "1 service" rather than "1 service(s)" — is decided
// here, because it is a property of the CLI and not of deploying.

/** How each `CLAUDE.md` outcome reads. A table so the four cases stay visibly parallel. */
const SEGMENT_WORDING: Record<SegmentOutcome, string> = {
  created  : 'Created the FlowRunner instructions block',
  inserted : 'Added the FlowRunner instructions block',
  replaced : 'Refreshed the FlowRunner instructions block',
  unchanged: 'The FlowRunner instructions block was already up to date',
}

/** A path as the user would type it, falling back to the absolute one when relative reads worse. */
function readablePath(fullPath: string): string {
  const relative = path.relative(process.cwd(), fullPath)

  return !relative || relative.startsWith('..') ? fullPath : relative
}

/** How each planned service reads. A table so the three cases stay visibly parallel. */
const STATUS_WORDING: Record<ServiceStatus, string> = {
  new     : 'new service',
  modified: 'modified service',
  same    : 'same service',
}

/** `qa-test (Qa Test)`, or just the id when the display name adds nothing. */
function nameService({ serviceId, service }: PlannedService): string {
  const displayName = service.serviceInfo.integrationName

  return displayName && displayName !== serviceId ? `${ serviceId } (${ displayName })` : serviceId
}

export function reportDeploy(event: DeployEvent): void {
  switch (event.type) {
    case 'connecting':
      section(`  Connecting to ${ describeServer(event.serverUrl) }`)
      say(`  Workspace ${ describeWorkspace(event.workspace) }`)

      return

    case 'discovered':
      say(`  Found ${ plural(event.planned.length, 'service') } under services/.`)

      return

    case 'skipped':
      sayWarning(`  Skipping "${ event.skipped.serviceId }" — ${ event.skipped.reason }`)

      return

    case 'selected':
      section(`  Deploying ${ plural(event.planned.length, 'service') }:`)

      return

    case 'icon-missing':
      sayWarning(`  Warning: icon file not found: services/${ event.serviceId }/public/${ event.relativePath }`)

      return

    case 'deployed': {
      const { entry } = event

      say(`    - ${ STATUS_WORDING[entry.status] }: ${ entry.serviceId } (${ entry.serviceName })` +
        ` - [${ shortHash(entry.sourceHash) }] - packaged ${ formatBytes(entry.packageBytes) }`)

      return
    }

    case 'unchanged':
      // Named rather than omitted: "this one was already there" is the answer to why it took no time.
      say(`    - ${ STATUS_WORDING.same }: ${ nameService(event.entry) }` +
        ` - [${ shortHash(event.entry.sourceHash) }] - already deployed, nothing to upload`)
  }
}

export function reportPull(event: PullEvent): void {
  switch (event.type) {
    case 'listed':
      section(`  Workspace ${ describeWorkspace(event.workspace) } has ${ plural(event.services.length, 'service') }.`)

      return

    case 'selected': {
      if (event.services.length === 0) {
        return
      }

      section(`  Pulling ${ plural(event.services.length, 'service') }:`)

      return
    }

    case 'up-to-date':
      // Reported, not omitted: "checked, and it already matches" is the answer to the question that was asked.
      say(`    ${ event.serviceId }: already up to date (${ event.sourceHash.slice(0, 8) })`)

      return

    case 'skipped':
      // Not an error: leaving local work alone is the safe default, and --force is the way to say otherwise.
      sayWarning(`    ${ event.serviceId }: skipped — ${ event.reason }. Pass --force to overwrite.`)

      return

    case 'pulled':
      say(`    ${ event.serviceId }: ${ plural(event.files, 'file') } into services/${ event.serviceId }/ (${ event.sourceHash.slice(0, 8) })`)

      return

    case 'install-needed':
      // Reported rather than installed — see operations/pull/extract.ts.
      sayWarning(`    ${ event.serviceId }: declares ${ plural(event.dependencies.length, 'dependency', 'dependencies') }` +
        ` — run \`npm install\` in services/${ event.serviceId }/`)
  }
}

export function reportLogin(event: LoginEvent): void {
  switch (event.type) {
    case 'connecting':
      say(`  Connecting this project to a Flowrunner workspace on ${ event.serverUrl }.`)

      return

    case 'authorize':
      section('  Authorize the CLI in your browser and pick the workspace to deploy to:')
      say('')
      say(`    ${ event.url }`)
      say('')

      return

    case 'waiting':
      section('  Waiting for authorization...')

      return

    case 'exchanging':
      say('  Exchanging code for token...')

      return

    case 'connected':
      section(`  This project is now connected to the workspace ${ describeWorkspace(event) }.`)
      say('  Written to flowrunner.json — commit it, and keep .flowrunner/token out of git.')
      say('')
  }
}

export function reportLogout(event: LogoutEvent): void {
  switch (event.type) {
    case 'starting':
      say('Logging out...')
      say('')

      return

    case 'revoked':
      say('  Token revoked on server.')

      return

    case 'cleared':
      say('  Auth cleared.')
  }
}

export function reportInit(event: InitEvent): void {
  switch (event.type) {
    case 'target':
      section('Project directory:')
      say('')
      say(`  ${ event.projectRoot }`)

      for (const line of event.lines) {
        say(`  ${ line }`)
      }

      say('')

      return

    case 'files':
      say('')

      for (const file of event.created) {
        say(`  + ${ file }`)
      }

      for (const file of event.skipped) {
        say(`    ${ file } — already there, left alone`)
      }

      section(`  Server: ${ event.serverUrl }`)

      return

    case 'repository':
      section(event.committed
        ? '  Git repository initialised, first commit made.'
        : '  Git repository initialised.')

      return

    case 'cancelled':
      section('Nothing was written.')
      say('')

      return

    case 'warning':
      sayWarning(`  ${ event.message }`)
  }
}

export function reportInitClaude(event: InitClaudeEvent): void {
  switch (event.type) {
    case 'agents': {
      const { created, updated, unchanged } = event

      section(`Agents in ${ readablePath(event.directory) }:`)
      say('')

      for (const agent of created) {
        say(`  + ${ agent }`)
      }

      for (const agent of updated) {
        say(`  ~ ${ agent } (updated)`)
      }

      for (const agent of unchanged) {
        say(`    ${ agent } — already up to date`)
      }

      return
    }

    case 'segment':
      // Named by outcome rather than "written": the whole promise of the marked block is that the rest of
      // the file was not touched, and only the verb says so.
      section(`  ${ SEGMENT_WORDING[event.outcome] } in ${ path.basename(event.file) }.`)

      return

    case 'warning':
      sayWarning(`  ${ event.message }`)
  }
}

export function reportCreateService(event: CreateServiceEvent): void {
  switch (event.type) {
    case 'template':
      section(`Creating a service from the "${ event.template.manifest.label }" template.`)
      say('')

      return

    case 'created-services-directory':
      section('  Created services/')

      return

    case 'created':
      section(`  Created ${ event.relativePath }/`)

      for (const file of event.files) {
        say(`    ${ file }`)
      }
  }
}
